import React, { createContext, useContext, useState, useEffect } from 'react';
import type { AuthState, User, LoginCredentials } from '../types/shelter';
import { API_BASE_URL } from '../config/runtime';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    // Check for existing session on app load
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('shelter-token');
        const userData = localStorage.getItem('shelter-user');
        
        if (token && userData) {
          const user = JSON.parse(userData) as User;
          
          // Verify token is still valid by making a request to the profile endpoint
          const response = await fetch(`${API_BASE_URL}/session/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const profileData = await response.json();
            if (profileData.success) {
              setAuthState({
                user: profileData.data,
                isAuthenticated: true,
                isLoading: false,
              });
              return;
            }
          }
          
          // Token is invalid, clear storage
          localStorage.removeItem('shelter-token');
          localStorage.removeItem('shelter-user');
        }
        
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('shelter-token');
        localStorage.removeItem('shelter-user');
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const response = await fetch(`${API_BASE_URL}/session/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const { user, token } = data.data;
        
        localStorage.setItem('shelter-token', token);
        localStorage.setItem('shelter-user', JSON.stringify(user));
        
        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        throw new Error(data.message || data.error || 'Login failed');
      }
    } catch (error) {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('shelter-token');
    localStorage.removeItem('shelter-user');
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
